import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BadgePercent, Flame, PiggyBank, Tag, Timer, SearchX } from 'lucide-react';
import { asArray, cn, formatPrice } from '@/lib/utils';
import ProductCard from '@/components/ProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORTS = [
  { value: 'discount', label: 'الأعلى خصماً' },
  { value: 'saving', label: 'الأكثر توفيراً' },
  { value: 'price_asc', label: 'السعر: من الأقل' },
  { value: 'price_desc', label: 'السعر: من الأعلى' },
  { value: 'ending', label: 'ينتهي قريباً' },
];

const DISCOUNT_LEVELS = [
  { value: 0, label: 'كل الخصومات' },
  { value: 20, label: '20% فأكثر' },
  { value: 30, label: '30% فأكثر' },
];

const saving = (p) => (p.original_price || 0) - p.price;

// عدّاد لأقرب عرض هينتهي (في البانر)
const NearestEnding = ({ endsAt }) => {
  const [left, setLeft] = useState(() => new Date(endsAt).getTime() - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(endsAt).getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (left <= 0) return null;
  const s = Math.floor(left / 1000);
  const parts = [
    ...(s >= 86400 ? [[Math.floor(s / 86400), 'يوم']] : []),
    [Math.floor((s % 86400) / 3600), 'ساعة'],
    [Math.floor((s % 3600) / 60), 'دقيقة'],
    [s % 60, 'ثانية'],
  ];
  return (
    <div className="inline-flex flex-wrap items-center justify-center gap-3 rounded-2xl bg-black/20 ring-1 ring-white/10 backdrop-blur px-4 py-3">
      <span className="flex items-center gap-2 font-cairo text-sm text-white/85">
        <Timer className="w-4 h-4 text-brand-gold" /> أقرب عرض ينتهي خلال
      </span>
      <span className="flex items-center gap-1.5" dir="rtl">
        {parts.map(([v, label], i) => (
          <span key={label} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-white/40">:</span>}
            <span className="flex flex-col items-center">
              <span className="min-w-9 h-9 px-1.5 rounded-lg bg-white/10 font-tajawal font-extrabold text-lg tabular-nums flex items-center justify-center">
                {String(v).padStart(2, '0')}
              </span>
              <span className="text-[10px] text-white/60 mt-0.5">{label}</span>
            </span>
          </span>
        ))}
      </span>
    </div>
  );
};

const Stat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-3 rounded-2xl bg-white/10 ring-1 ring-white/10 backdrop-blur px-4 py-3">
    <span className="w-10 h-10 rounded-xl bg-brand-gold text-brand-black flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" />
    </span>
    <div className="text-right">
      <p className="font-tajawal font-extrabold text-xl leading-none">{value}</p>
      <p className="font-cairo text-xs text-white/70 mt-1">{label}</p>
    </div>
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'shrink-0 h-9 px-4 rounded-full text-sm font-cairo font-semibold border transition-all',
      active
        ? 'bg-burgundy-500 border-burgundy-500 text-white shadow-sm'
        : 'bg-white border-gray-200 text-gray-700 hover:border-burgundy-500 hover:text-burgundy-500'
    )}
  >
    {children}
  </button>
);

const Offers = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [minDiscount, setMinDiscount] = useState(0);
  const [sort, setSort] = useState('discount');

  const load = () =>
    axios
      .get(`${API}/products`, { params: { on_sale: 1, sort: 'discount', per_page: 60 } })
      .then((res) => setProducts(asArray(res.data?.data?.products)))
      .catch((err) => console.error('Failed to fetch offers:', err))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    axios
      .get(`${API}/products/filters`)
      .then((res) => setCategories(asArray(res.data?.data?.categories)))
      .catch(() => {});
  }, []);

  // أرقام حقيقية من العروض الحالية
  const stats = useMemo(() => {
    const ends = products.map((p) => p.sale_ends_at).filter((d) => d && new Date(d) > new Date());
    return {
      count: products.length,
      maxDiscount: Math.max(0, ...products.map((p) => p.discount_percentage || 0)),
      maxSaving: Math.max(0, ...products.map(saving)),
      nearestEnd: ends.sort()[0] || null,
    };
  }, [products]);

  // الأقسام اللي فيها عروض بس، مع عدد العروض في كل قسم
  const offerCategories = useMemo(() => {
    const counts = products.reduce((acc, p) => ({ ...acc, [p.category_id]: (acc[p.category_id] || 0) + 1 }), {});
    return categories.filter((c) => counts[c.id]).map((c) => ({ ...c, offers: counts[c.id] }));
  }, [products, categories]);

  const visible = useMemo(() => {
    const list = products.filter(
      (p) => (category === 'all' || p.category_id === category) && (p.discount_percentage || 0) >= minDiscount
    );
    const by = {
      discount: (a, b) => (b.discount_percentage || 0) - (a.discount_percentage || 0),
      saving: (a, b) => saving(b) - saving(a),
      price_asc: (a, b) => a.price - b.price,
      price_desc: (a, b) => b.price - a.price,
      ending: (a, b) => (a.sale_ends_at ? new Date(a.sale_ends_at).getTime() : Infinity) - (b.sale_ends_at ? new Date(b.sale_ends_at).getTime() : Infinity),
    };
    return [...list].sort(by[sort]);
  }, [products, category, minDiscount, sort]);

  const hasFilters = category !== 'all' || minDiscount > 0;

  return (
    <div className="pb-16 bg-gray-50/60 dark:bg-transparent min-h-screen">
      {/* البانر */}
      <section className="relative overflow-hidden bg-gradient-to-l from-burgundy-600 via-burgundy-500 to-[#3d0a0f] text-white">
        <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(212,175,55,0.22) 1px, transparent 1.4px)', backgroundSize: '22px 22px' }} />
        <div className="absolute -left-24 -top-24 w-[380px] h-[380px] rounded-full border-[36px] border-brand-gold/10" />
        <span className="absolute left-6 md:left-24 bottom-0 translate-y-1/4 font-tajawal font-extrabold text-[12rem] md:text-[18rem] leading-none text-brand-gold/10 select-none">%</span>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px] py-12 md:py-16 text-center">
          <p className="inline-flex items-center gap-2 bg-white/10 ring-1 ring-white/15 rounded-full px-4 py-1.5 text-sm font-cairo font-bold text-brand-gold mb-5">
            <Flame className="w-4 h-4" /> عروض مميزة
          </p>
          <h1 className="font-tajawal font-extrabold text-4xl md:text-6xl leading-[1.3] mb-3">العروض والتخفيضات</h1>
          <p className="font-cairo text-white/80 md:text-lg mb-8">قطع مختارة بأسعار مخفّضة — اختاري قطعتك ووفّري أكثر</p>

          {loading ? (
            <div className="flex justify-center gap-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-40 rounded-2xl bg-white/10" />)}
            </div>
          ) : stats.count > 0 ? (
            <div className="space-y-5">
              <div className="flex flex-wrap justify-center gap-3">
                <Stat icon={Tag} value={stats.count} label="قطعة عليها خصم" />
                <Stat icon={BadgePercent} value={`${stats.maxDiscount}%`} label="أعلى نسبة خصم" />
                <Stat icon={PiggyBank} value={formatPrice(stats.maxSaving)} label="وفّري حتى" />
              </div>
              {stats.nearestEnd && <NearestEnding endsAt={stats.nearestEnd} />}
            </div>
          ) : null}
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
        {/* الفلاتر */}
        {!loading && products.length > 0 && (
          <div className="sticky top-[5.5rem] md:top-[7.5rem] z-20 -mx-4 sm:mx-0 mt-6 mb-6 px-4 sm:px-4 py-3 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md sm:rounded-2xl border-y sm:border border-gray-200/80 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0">
                <Chip active={category === 'all'} onClick={() => setCategory('all')}>
                  الكل <span className="opacity-70">({products.length})</span>
                </Chip>
                {offerCategories.map((c) => (
                  <Chip key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
                    {c.name} <span className="opacity-70">({c.offers})</span>
                  </Chip>
                ))}
              </div>
              <div className="flex items-center gap-2 shrink-0 overflow-x-auto no-scrollbar">
                {DISCOUNT_LEVELS.map((d) => (
                  <Chip key={d.value} active={minDiscount === d.value} onClick={() => setMinDiscount(d.value)}>
                    {d.label}
                  </Chip>
                ))}
                <Select value={sort} onValueChange={setSort} dir="rtl">
                  <SelectTrigger className="w-[170px] h-9 rounded-full bg-white border-gray-200 font-cairo text-sm shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="font-cairo">
                    {SORTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mt-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-gray-200/80 bg-white overflow-hidden">
                <Skeleton className="aspect-[3/4] w-full rounded-none" />
                <div className="p-4 space-y-2"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 mt-8 rounded-3xl bg-white border border-dashed border-gray-300">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-burgundy-500/10 flex items-center justify-center">
              <BadgePercent className="w-8 h-8 text-burgundy-500" />
            </div>
            <h2 className="font-tajawal font-bold text-xl text-brand-black mb-2">لا توجد عروض حالياً</h2>
            <p className="font-cairo text-gray-500 mb-6">تابعينا، العروض الجديدة بتنزل باستمرار</p>
            <Link to="/products" className="inline-flex items-center h-11 px-6 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-white font-cairo font-semibold transition">
              تصفحي كل المنتجات
            </Link>
          </div>
        ) : (
          <>
            <p className="font-cairo text-sm text-gray-600 mb-4">
              <span className="font-bold text-brand-black">{visible.length}</span> عرض
              {hasFilters && (
                <button type="button" onClick={() => { setCategory('all'); setMinDiscount(0); }} className="mr-3 text-burgundy-500 font-semibold hover:underline">
                  مسح الفلاتر
                </button>
              )}
            </p>
            {visible.length === 0 ? (
              <div className="text-center py-16 rounded-3xl bg-white border border-dashed border-gray-300">
                <SearchX className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <p className="font-cairo text-gray-500">مفيش عروض بالفلاتر دي</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                {visible.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Offers;
